import { useEffect, useState } from 'react';
import { extractFontFamiliesFromHtml } from '@shared/core/website/theming/extractFontFamiliesFromHtml';

/**
 * FOUT guard for the rich header title: render the initial HTML immediately (SSR/first paint —
 * font-display: swap covers it), but for later changes hold the previous HTML until every family
 * the new one ADDS has loaded (useEventFontsLoader loads them; accumulation keeps old families
 * available). Applying inline font-family before the file is ready flashes the serif fallback —
 * same guard GuestSiteTypographyOverride uses for the pack fonts. Waiting only on newly
 * referenced families keeps typing responsive right after a font pick, and clears immediately
 * when the title goes away (e.g. flag flipped off).
 *
 * `hasFont`/`loadedFonts` come from useEventDesignThemingProvider — passed in so the state
 * machine is unit-testable without the theming context.
 */
export const useFontReadyTitleHtml = (titleHtml: string | undefined, hasFont: (family: string) => boolean, loadedFonts: ReadonlySet<string>): string | undefined => {
  const [renderedTitleHtml, setRenderedTitleHtml] = useState(titleHtml);

  useEffect(() => {
    if (titleHtml === renderedTitleHtml) return;
    const renderedFamilies = new Set(renderedTitleHtml ? extractFontFamiliesFromHtml(renderedTitleHtml) : []);
    const newFamilies = titleHtml ? extractFontFamiliesFromHtml(titleHtml).filter(family => !renderedFamilies.has(family)) : [];
    if (newFamilies.every(hasFont)) {
      setRenderedTitleHtml(titleHtml);
    }
    // loadedFonts re-runs the check when WebFont reports a family active/failed.
  }, [titleHtml, renderedTitleHtml, hasFont, loadedFonts]);

  return renderedTitleHtml;
};
